/* eslint-disable @typescript-eslint/no-unused-vars */
'use client';

import { motion, useMotionValue, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { getSectionMotionHeading } from '@/components/dynamic-zone/section-motion-heading';
import { Card } from '@/components/ui/card';
import { components } from '@/types/strapi';
import { SectionContainer } from '../../ui/SectionContainer';

// Custom card component with advanced hover effects
const FeatureCard = ({
	feature,
	index,
}: {
	feature: components['schemas']['ItemsAnimatedFeaturesComponent'];
	index: number;
}) => {
	const [hovered, setHovered] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	// Create motion values for 3D rotation effect
	const mouseX = useMotionValue(0);
	const mouseY = useMotionValue(0);

	const rotateX = useTransform(mouseY, [-100, 100], [5, -5]);
	const rotateY = useTransform(mouseX, [-100, 100], [-5, 5]);

	// Handle mouse move for 3D effect
	const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
		if (!ref.current) return;
		const rect = ref.current.getBoundingClientRect();

		const x = e.clientX - rect.left - rect.width / 2;
		const y = e.clientY - rect.top - rect.height / 2;

		mouseX.set(x);
		mouseY.set(y);
	};

	return (
		<Card className="relative h-full overflow-hidden border border-gray-100/20 bg-white/50 p-4 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] backdrop-blur-sm transition-all duration-300 hover:border-gray-200/30 hover:shadow-[0_8px_30px_-4px_rgba(0,0,0,0.2)]">
			{/* Animated gradient background */}
			<motion.div
				className="absolute inset-0 bg-gradient-to-br opacity-0 transition-opacity duration-300 group-hover:opacity-100"
				animate={{ opacity: hovered ? 1 : 0 }}
				transition={{ duration: 0.4 }}>
				<div
					className={`absolute inset-0 opacity-50`}
					style={{
						background: `linear-gradient(to bottom right, ${feature.ligth_color_from}, ${feature.light_color_to})`,
					}}></div>
			</motion.div>

			{/* Content */}
			<div className="relative z-10">
				{/* Icon with gradient background and glow effect */}
				<div className="relative mb-4">
					<motion.div
						className={`absolute inset-0 rounded-2xl opacity-20 blur-xl transition-opacity duration-300`}
						style={{
							background: `linear-gradient(to bottom right, ${feature.image_color_from}, ${feature.image_color_to})`,
						}}
						animate={{
							opacity: hovered ? 0.5 : 0.2,
							scale: hovered ? 1.1 : 1,
						}}
						transition={{ duration: 0.4 }}
					/>
					{feature.icon?.url && (
						<div
							className={`relative flex h-12 w-12 items-center justify-center rounded-2xl`}
							style={{
								background: `linear-gradient(to bottom right, ${feature.image_color_from}, ${feature.image_color_to})`,
							}}>
							<Image
								src={feature.icon.url}
								alt={feature.icon?.name || `logo${index}`}
								width={5}
								height={5}
								className="h-6 w-6 object-contain"
							/>
						</div>
					)}
				</div>

				<motion.h3
					className="mb-2 text-lg font-semibold text-gray-900"
					animate={{
						color: hovered ? '#1e293b' : '#1f2937',
						y: hovered ? -2 : 0,
					}}
					transition={{ duration: 0.2 }}>
					{feature.title}
				</motion.h3>

				<motion.p
					className="mb-3 text-sm text-gray-600"
					animate={{ opacity: hovered ? 0.9 : 0.7 }}
					transition={{ duration: 0.3 }}>
					{feature.description}
				</motion.p>

				{/* Know More button with enhanced animation */}
				<Link
					href={feature.href || '/'}
					className="group inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700">
					<motion.span
						animate={{
							color: hovered ? '#2563eb' : '#2563eb',
						}}>
						Know More
					</motion.span>
					<motion.div
						animate={{ x: hovered ? 5 : 0 }}
						transition={{
							type: 'spring',
							stiffness: 400,
							damping: 10,
						}}>
						<ArrowRight className="ml-1 h-4 w-4" />
					</motion.div>
				</Link>
			</div>
		</Card>
	);
};

export function AnimatedFeatureGrid({
	heading,
	sub_heading,
	feature_cards,
	isFirst = false,
}: components['schemas']['DynamicZoneAnimatedFeatureGridComponent'] & {
	isFirst?: boolean;
}) {
	const SectionHeading = getSectionMotionHeading(isFirst);
	const [ref, inView] = useInView({
		threshold: 0.1,
		triggerOnce: true,
	});

	// Variants for staggered animations
	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.1,
				delayChildren: 0.3,
			},
		},
	};

	// Variants for headline animations
	const headlineVariants = {
		hidden: { opacity: 0, y: 30 },
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				type: 'spring',
				stiffness: 100,
				damping: 15,
			},
		},
	};

	return (
		<SectionContainer
			withBackground
			className="animated-feature-grid-section">
			<noscript>
				<style>{`
					.animated-feature-grid-section h2[style],
					.animated-feature-grid-section h3[style],
					.animated-feature-grid-section p[style],
					.animated-feature-grid-section div[style*='opacity'],
					.animated-feature-grid-section div[style*='scale'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			<div className="container relative mx-auto px-4 py-12">
				{/* Decorative background elements */}
				<div className="absolute inset-0 overflow-hidden">
					<div className="absolute -left-10 -top-10 h-40 w-40 rounded-full bg-blue-100 opacity-20 blur-3xl"></div>
					<div className="absolute bottom-10 right-10 h-60 w-60 rounded-full bg-purple-100 opacity-20 blur-3xl"></div>
				</div>

				{/* Header section with enhanced animations */}
				<motion.div
					className="relative mx-auto mb-6 max-w-3xl text-center"
					initial="hidden"
					animate="visible"
					variants={containerVariants}>
					<SectionHeading
						className="mb-4 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-4xl font-extrabold tracking-tight sm:text-5xl"
						variants={headlineVariants}>
						{heading}
					</SectionHeading>

					<motion.p
						className="text-lg text-gray-600"
						variants={headlineVariants}>
						{sub_heading}
					</motion.p>
				</motion.div>
				<motion.div
					ref={ref}
					className="relative grid gap-6 md:grid-cols-2 lg:grid-cols-3"
					variants={containerVariants}
					initial="hidden"
					animate={inView ? 'visible' : 'hidden'}>
					{feature_cards?.map((feature, index) => (
						<FeatureCard
							key={index}
							feature={feature}
							index={index}
						/>
					))}
				</motion.div>
			</div>
		</SectionContainer>
	);
}
